import { useState, useEffect } from "react";
import { apiFetch } from "../../../core/utils/api";
import { generateEditUrl } from '../../../core/utils/urlUtils';
import { getNewSubscriptionsAllowedSafe } from "../../../core/utils/subscriptionConfigApi";
import { API_ENDPOINTS } from "../constants";

/**
 * Custom hook to manage website publish state and workflow
 */
export const usePublishState = (website, websiteId) => {
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [hasPublished, setHasPublished] = useState(Boolean(website?.is_published));
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [publishLimitInfo, setPublishLimitInfo] = useState(null);

  // Determine if the container has any published version (not just the current one)
  useEffect(() => {
    const fetchContainerPublishState = async () => {
      try {
        if (!website?.container_id) {
          setHasPublished(Boolean(website?.is_published));
          return;
        }
        const res = await apiFetch(API_ENDPOINTS.CONTAINER_VERSIONS(website.container_id), { credentials: 'include' });
        if (!res.ok) {
          setHasPublished(Boolean(website?.is_published));
          return;
        }
        const data = await res.json();
        const anyPublished = Array.isArray(data?.versions) && data.versions.some(v => v.is_published);
        setHasPublished(anyPublished);
      } catch (_) {
        setHasPublished(Boolean(website?.is_published));
      }
    };
    fetchContainerPublishState();
  }, [website?.container_id, website?.is_published]);

  const handlePublish = async () => {
    if (!websiteId) return;
    try {
      // Gate publishing by plan only when publishing would increase the number of published websites.
      // If this container already has a published version, publishing again is a replace, not a new published website.
      if (!hasPublished) {
        const res = await apiFetch("/api/subscription/limits/", { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.limits_reached?.published) {
          setPublishLimitInfo({
            plan_display_name: data?.plan?.display_name || data?.plan?.name || "your plan",
            max_published_websites: data?.plan?.max_published_websites,
            current_published: data?.usage?.current_published,
          });
          setUpgradeModalOpen(true);
          return;
        }
      }
    } catch (_) {
      // If the gate check fails, we allow the user to proceed and let backend validations handle it.
    }
    setPublishConfirmOpen(true);
  };

  const confirmPublish = async () => {
    if (!websiteId) return;
    setPublishing(true);
    try {
      // 1) Create a new draft cloned from the current version (force_new=true ensures cloning
      //    from THIS version rather than reusing any existing draft)
      const prepRes = await apiFetch(API_ENDPOINTS.PREPARE_FOR_EDIT(websiteId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ force_new: true })
      });
      if (!prepRes.ok) throw new Error("Failed to prepare new draft version");
      const data = await prepRes.json();

      // 2) Publish the current version after the clone is successfully created
      const publishRes = await apiFetch(API_ENDPOINTS.PUBLISH(websiteId), {
        method: "POST",
        credentials: "include",
      });
      if (!publishRes.ok) throw new Error("Publish failed");

      // Close modal before redirect
      setPublishConfirmOpen(false);

      // Fetch subscription current to see if we should show expiring warning
      try {
        const subRes = await apiFetch("/api/subscription/current/", { credentials: "include" });
        if (subRes.ok) {
          const subData = await subRes.json();
          const isExpiring = subData.plan !== "free" && subData.is_active && !subData.will_renew;
          const isAccessGrant = Boolean(subData.access_grant?.is_active);

          if (isExpiring || isAccessGrant) {
            // Check whether new subscriptions/upgrades are currently allowed platform-wide.
            // When disabled, the warning should not push an "Upgrade" CTA; instead, it
            // should let the user know paid plans are coming soon.
            const subscriptionsAllowed = await getNewSubscriptionsAllowedSafe();

            sessionStorage.setItem('expiringPlan', JSON.stringify({
              isAccessGrant,
              isExpiring,
              planDisplayName: (isAccessGrant ? subData.access_grant?.plan_display_name : subData.plan_display_name) || "Plus plan",
              expirationDate: isAccessGrant ? subData.access_grant?.expires_at : subData.current_period_end,
              subscriptionsDisabled: !subscriptionsAllowed,
            }));
          }
        }
      } catch (_) {
        // Ignore errors fetching sub info here
      }

      // Mark just-published to show a brief success toast after redirect
      try { sessionStorage.setItem('justPublished', 'true'); } catch (_) {}

      // Navigate to the new draft version's edit URL
      const params = data.edit_url_params;
      if (params?.username && params?.domain && params?.version_slug) {
        const url = generateEditUrl(params.username, params.domain, params.version_slug);
        window.location.href = url;
      } else {
        alert("Published, but could not open new draft version.");
      }
    } catch (e) {
      setPublishConfirmOpen(false);
      alert(e.message);
    } finally {
      setPublishing(false);
    }
  };

  return {
    publishConfirmOpen,
    setPublishConfirmOpen,
    publishing,
    hasPublished,
    handlePublish,
    confirmPublish,
    upgradeModalOpen,
    setUpgradeModalOpen,
    publishLimitInfo
  };
};

